"use client";

import type { Editor } from "@tiptap/core";
import type { Attrs, Node as PMNode, NodeType } from "@tiptap/pm/model";
import { Columns3, Images, ListOrdered, type LucideIcon } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useLocale } from "@/hooks/useLocale";
import { t } from "@/lib/localization";
import {
    SCENE_BLOCK_REGISTRY,
    SCENE_BLOCK_TYPES,
    type SceneBlockType,
} from "./registry";

/** UI-only metadata (icon + label) for each block type. Lives here, not in the
 *  framework-agnostic registry. A new block type added to the registry must be
 *  added here too — the exhaustive Record makes that a compile error. */
const BLOCK_TYPE_META: Record<
    SceneBlockType,
    { icon: LucideIcon; labelKey: string }
> = {
    numberedSteps: {
        icon: ListOrdered,
        labelKey: "lomda.scene-block.type.numbered-steps",
    },
    outlineCards: {
        icon: Columns3,
        labelKey: "lomda.scene-block.type.outline-cards",
    },
    imageGrid: { icon: Images, labelKey: "lomda.scene-block.type.image-grid" },
};

/** Keep only the attrs the target node type actually defines, so converting
 *  between blocks never throws on an unsupported attribute. Shared text attrs
 *  (textAlign, textDirection) carry over; block-specific ones are dropped. */
function pickAttrs(type: NodeType, src: Attrs | null | undefined): Attrs {
    const out: Record<string, unknown> = {};
    if (!src) return out;
    // NodeType carries its attribute map at runtime but it isn't in the types.
    const valid = (type as unknown as { attrs: Record<string, unknown> }).attrs;
    for (const key of Object.keys(valid)) {
        if (key in src) out[key] = src[key];
    }
    return out as Attrs;
}

/**
 * Convert a custom block to another type in place, preserving its items. All
 * custom blocks share the same shape — a list of items, each with a title, a
 * body, and an optional image — so this single converter is registry-driven and
 * works for any pair (and any future block that fits the shape).
 *
 * Item title/body inline content (and text alignment) is carried over verbatim;
 * the image is kept only when the target supports one; block-level style attrs
 * reset to the target's defaults. One transaction → one undo step.
 */
function convertBlock(
    editor: Editor,
    pos: number,
    node: PMNode,
    targetType: SceneBlockType
) {
    const target = SCENE_BLOCK_REGISTRY[targetType];
    const { schema } = editor;
    const titleType = schema.nodes[target.titleField.nodeName];
    const bodyType = schema.nodes[target.bodyField.nodeName];
    const itemType = schema.nodes[target.itemNodeName];
    const blockType = schema.nodes[target.nodeName];
    if (!titleType || !bodyType || !itemType || !blockType) return;

    const items: PMNode[] = [];
    node.forEach((item) => {
        const srcTitle = item.maybeChild(0);
        const srcBody = item.maybeChild(1);
        const title = titleType.create(
            pickAttrs(titleType, srcTitle?.attrs),
            srcTitle?.content
        );
        const body = bodyType.create(
            pickAttrs(bodyType, srcBody?.attrs),
            srcBody?.content
        );
        const itemAttrs = pickAttrs(itemType, {
            image: (item.attrs.image as string | null | undefined) ?? null,
        });
        items.push(itemType.create(itemAttrs, [title, body]));
    });

    const newBlock = blockType.createAndFill(null, items);
    if (!newBlock) return;
    const tr = editor.state.tr.replaceWith(pos, pos + node.nodeSize, newBlock);
    editor.view.dispatch(tr);
}

/** Dropdown of all registered block types; picking one converts in place. */
export function BlockTypeSwitcher({
    editor,
    getPos,
    node,
}: {
    editor: Editor;
    getPos: () => number | undefined;
    node: PMNode;
}) {
    const { locale, dir } = useLocale();
    const current = node.type.name as SceneBlockType;

    const onSelect = (target: SceneBlockType) => {
        if (target === current) return;
        const pos = getPos();
        if (pos == null) return;
        convertBlock(editor, pos, node, target);
    };

    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">
                {t("lomda.scene-block.block-type", locale)}
            </span>
            <Select
                dir={dir}
                value={current}
                onValueChange={(v) => onSelect(v as SceneBlockType)}
            >
                <SelectTrigger className="h-8 w-full">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {SCENE_BLOCK_TYPES.map((type) => {
                        const { icon: Icon, labelKey } = BLOCK_TYPE_META[type];
                        return (
                            <SelectItem key={type} value={type}>
                                <span className="flex items-center gap-2">
                                    <Icon className="size-4 shrink-0" />
                                    {t(labelKey, locale)}
                                </span>
                            </SelectItem>
                        );
                    })}
                </SelectContent>
            </Select>
        </div>
    );
}
